"use client";

import { useCallback, useState } from "react";

import type { OrderHistoryResponse } from "@/app/ec/product/[basePath]/purchase-history";
import getCategoryTreeClient from "@/fetch/getCategoryTreeClient";
import getPurchaseHistoryClient from "@/fetch/getPurchaseHistoryClient";

/**
 * Loads the Order History modal's data on demand: call `load` when the modal opens. The first call
 * fetches; later calls reuse the data, or retry after a failure.
 */
export default function useLazyPurchaseHistory(mpId: number) {
  const [purchaseHistory, setPurchaseHistory] = useState<OrderHistoryResponse | null>(null);
  const [categoriesTree, setCategoriesTree] = useState<CategoryTree[] | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (purchaseHistory !== null || isFetching) {
      return;
    }
    setIsFetching(true);
    setLoadFailed(false);
    try {
      const [history, tree] = await Promise.all([getPurchaseHistoryClient(mpId), getCategoryTreeClient(mpId)]);
      if (history) {
        setPurchaseHistory(history);
        setCategoriesTree(tree ?? []);
      } else {
        setLoadFailed(true);
      }
    } catch {
      setLoadFailed(true);
    } finally {
      setIsFetching(false);
    }
  }, [mpId, purchaseHistory, isFetching]);

  return { purchaseHistory, categoriesTree: categoriesTree ?? [], loadFailed, load };
}
